
const UNIT_Y = new THREE.Vector3(0, 1, 0);

// Transform 3D model position to pixel-space position on full MiniMap. Indepdent of scroll-position.
const worldToMapPixels = (pos3D, mapMatrix, mapWidth, mapHeight) => {
    const p = pos3D.clone().applyProjection(mapMatrix);
    let res =  new THREE.Vector2(
        (( p.x + 1) / 2) * mapWidth,
        ((-p.y + 1) / 2) * mapHeight
    );
    return res;
};

const mapPixelsToWorld = (posX, posY, zWorld, mapMatrixInv, mapWidth, mapHeight) => {
    // convert pixels to normalized viewport coords in [-1,1]
    const vx = 2 * (posX / mapWidth)  - 1.0;
    const vy = 2 * (posY / mapHeight) - 1.0;

    // Beware of the annoying y-flip
    let p = new THREE.Vector3(vx, -vy, 0.0).applyProjection(mapMatrixInv);
    p.z = zWorld;
    return p;
};

// Checks whether the given 3D position would appear inside the map for the given mapMatrix.
//  @param {Vector3} pos3D
//  @param {Matrix4} mapMatrix
//  @returns {bool}
let _tmpVec = null;
const posIsInsideMap = (pos3D, mapMatrix) => {

    _tmpVec = _tmpVec || new THREE.Vector3();

    // Compute position in map space [-1, 1] that we would get when using this map
    const mapPos = _tmpVec.copy(pos3D).applyProjection(mapMatrix);

    // Check if camera position is inside
    return Math.abs(mapPos.x) < 1.0 && Math.abs(mapPos.y) < 1.0;
};

// Configures the ortho-camera from given 3 points, so that:
//  p0: appears at lower-left corner
//  p1: appears at lower-right corner
//  p2: appears at upper-left corner
//
// We assume that camera target.z is already set and on the sheet-plane level
const setCameraFromFrustumCorners = (camera, p0, p1, p2) => {

    // up-vector: p2-p0
    camera.up.x = p2.x - p0.x;
    camera.up.y = p2.y - p0.y;

    // position
    camera.position.x = 0.5 * (p1.x + p2.x);
    camera.position.y = 0.5 * (p1.y + p2.y);

    let frustumHeight = camera.up.length();

    // normalize up-vector
    camera.up.multiplyScalar(1.0 / frustumHeight);

    // For LMV ortho-cameras...
    // - The vertical frustum height is identical with the orthoScale
    // - The orthoScale is determined by the distance between position and target
    // - For sheets, the view direction is always -z and zoom is manipulated by changing pos.z while having a constant target.z
    // Therefore, we obtain the desired frustum height by:
    let targetDist = frustumHeight;
    camera.position.z = camera.target.z + targetDist;

    // keep horizontal target position in sync
    camera.target.x = camera.position.x;
    camera.target.y = camera.position.y;

    // This updates the camera quaternion. Without this, modifying the up-vector would not have any effect.
    Autodesk.Viewing.Navigation.prototype.orient(camera, camera.target, camera.position, camera.up);

    camera.dirty = true;
};

/**
 * Updates the camera for the top view
 * @param {Autodesk.Viewing.Camera} camera - Camera to be updated
 * @param {THREE.Box3} bbox - Bounding box of the area we want to view
 * @param {Number} zMin - Minimum of z range
 * @param {Number} zMax - Maximum of z range
 * @param {Number} orthoScale - Scale factor for orthographic view
 * @param {THREE.Vector3} [up] - Optional up vector, (0,1,0) will be used if not set
 * @param {THREE.Vector3} [worldup] - Optional world up vector, (0,1,0) will be used if not set
 * @returns True, if camera values changed
 */
const setTopView = (camera, bbox, zMin, zMax, orthoScale, up = UNIT_Y, worldup = UNIT_Y) => {

    const center = THREE.Box3.prototype.getCenter.call(bbox, new THREE.Vector3());

    // set camera pos to upper face center
    let camChanged = Math.abs(camera.position.x - center.x) > Number.EPSILON ||
    Math.abs(camera.position.y - center.y) > Number.EPSILON ||
    Math.abs(camera.position.z - zMax) > Number.EPSILON;

    camera.position.copy(center);
    camera.position.z = zMax;

    // target distance defines orthoScale, which finally
    // defines the height of the orthoCam frustum in world-space
    camChanged || (camChanged = Math.abs(camera.target.x - camera.position.x) > Number.EPSILON ||
                 Math.abs(camera.target.y - camera.position.y) > Number.EPSILON ||
                 Math.abs(camera.target.z - (camera.position.z - orthoScale)) > Number.EPSILON ||
                 camera.isPerspective);

    camera.target.copy(camera.position);
    camera.target.z -= orthoScale;
    camera.isPerspective = false;

    // set up-vector for view from top
    camChanged || (camChanged = Math.abs(camera.up.x - up.x) > Number.EPSILON ||
                   Math.abs(camera.up.y - up.y) > Number.EPSILON ||
                   Math.abs(camera.up.z - up.z) > Number.EPSILON ||
                   Math.abs(camera.worldup.x - worldup.x) > Number.EPSILON ||
                   Math.abs(camera.worldup.y - worldup.y) > Number.EPSILON ||
                   Math.abs(camera.worldup.z - worldup.z) > Number.EPSILON);

    camera.up.copy(up);
    camera.worldup.copy(worldup);

    // Configure near/far to see only zMin/zMax range
    camChanged || (camChanged = camera.near !== 0 || Math.abs(camera.far - (zMax - zMin)) > Number.EPSILON);

    camera.near = 0;
    camera.far  = zMax-zMin;

    // This makes sure that compute3DTo2DMatrix() produces always the same resuts, no matter whether we do it before or after rendering.
    camera.updateCameraMatrices();

    return camChanged;
};

// Computes a Matrix4 that transforms from 3D model position to
// normalized viewport coords ([-1,1]^2) on the minimap.
const compute3DTo2DMatrix = (camera, dstMatrix) => {
    dstMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
};

// Helper function to smoothen movement of scroll position and zoomScale.
//  @param {number} dist      - distance to move to reach target directly. Must be >=0.
//  @param {number} elapsed   - elapsed time in seconds
//  @param {number} maxSpeed  - maximum allowed distance units per second
//  @param {number} lastSpeed - motion speed according to last cycle (delta / elapsed)
//  @param {number} acc       - acceleration speed (in distance units per s^2)
//  @returns {number} Result is a distance <=dist, which is slowed down if needed for smoother movement.
const motionSmoothing = (dist, elapsed, maxSpeed, lastSpeed, acc) => {

    // Limit max scroll speed and avoid too sudden acceleration compared to last cycle
    let speed = Math.min(maxSpeed, lastSpeed + acc * elapsed);

    // Slow down with constant deceleration when approaching the target position:
    //
    //   With a constant acceleration a for a time of t seconds, we have:
    //     speed    = t*a
    //     distance = 0.5 * a * t^2
    //
    // To scroll a given distance d with constant acceleration a, the time needed is:
    //   t = sqrt(2*d / a)
    //
    // The speed reached at that point is:
    //   s = a * sqrt(2*d / a)
    //
    // Therefore, s as given above gives us the speed that we must have if distance d
    // to the target if we want to stop at the target given a constant deceleration of a.
    let slowDownSpeed = acc * Math.sqrt(2 * dist / acc);
    speed = Math.min(slowDownSpeed, speed);

    // restrict distance based on computed speed limit
    let maxDist  = speed * elapsed;
    return Math.min(dist, maxDist);
};

// Rotates p around the center point. Works for Vector3 in xy too.
//  @param {Vector2} p
//  @param {Vector2} center
//  @param {number}  angle  - counterclockwise angle in radians
const rotateAround = (p, center, angle) => {

    let c = Math.cos( angle );
    let s = Math.sin( angle );

    let x = p.x - center.x;
    let y = p.y - center.y;

    p.x = x * c - y * s + center.x;
    p.y = x * s + y * c + center.y;
};

// Expands rect in-place by expanding the smallest axis around the center.
//  @param {Box2} rect
const expandToSquare = (rect) => {

    // determine scale factor from max-axis of sheet bounds
    var diag   = rect.getSize(new THREE.Vector2());
    var center = rect.getCenter(new THREE.Vector2());

    var radius = 0.5 * Math.max(diag.x, diag.y);

    rect.min.set(center.x - radius, center.y - radius);
    rect.max.set(center.x + radius, center.y + radius);
};

// Rescales a point p within srcBounds to [0.1]^2. Works in-place.
//  @param {Vector2} p
//  @param {Box2} srcBounds
const boundsToUnitQuad = (p, srcBounds) => {
    var diagX = srcBounds.max.x - srcBounds.min.x;
    var diagY = srcBounds.max.y - srcBounds.min.y;

    p.x -= srcBounds.min.x;
    p.y -= srcBounds.min.y;

    p.x /= diagX;
    p.y /= diagY;
};

// Compute rotation angle for camera view-cone on map.
//  @param {THREE.Vector2} pos    - camera position     in minimap pixel space
//  @param {THREE.Vector2} target - camera target point in minimap pixel space
//  @returns {number}  Rotation angle in radians to transform a originally up-facing div located
//                     in a way that it points towards (p2-p1).
const computeAngle = (pos, target) => {
    const dx = target.x - pos.x;
    const dy = target.y - pos.y;
    let angle = Math.atan2(dy, dx);

    // atan measures the angle to the x-axis, but we want it to work for divs originally aligned with +y.
    angle += Math.PI / 2;

    return angle;
};

export {
    worldToMapPixels,
    mapPixelsToWorld,
    posIsInsideMap,
    setCameraFromFrustumCorners,
    setTopView,
    compute3DTo2DMatrix,
    motionSmoothing,
    rotateAround,
    expandToSquare,
    boundsToUnitQuad,
    computeAngle
};
